// File: src/components/admin/common/JsonViewTab.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
// All rights reserved.

import React, { useState, useEffect } from 'react';
import { Box, TextField, Alert, Button, Snackbar } from '@mui/material';
import { ContentCopy, Save } from '@mui/icons-material';

/**
 * A generic JSON view/edit tab.
 * 
 * @param {object} props
 * @param {object} props.data - The data object to display as JSON.
 * @param {function} props.onSave - Callback (newData) => void to update the parent state.
 * @param {boolean} props.readOnly - If true, editing is disabled.
 */
const JsonViewTab = ({ data, onSave, readOnly = false }) => {
    const [jsonText, setJsonText] = useState('');
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });

    // Sync local text with prop data when data changes externally
    useEffect(() => {
        if (data) {
            setJsonText(JSON.stringify(data, null, 2));
        }
    }, [data]);

    const handleChange = (e) => {
        setJsonText(e.target.value);
        setError(null);
    };

    const handleApply = () => {
        try {
            const parsed = JSON.parse(jsonText);
            onSave(parsed);
            setSnackbar({ open: true, message: 'JSON applied to form!' });
        } catch (err) {
            setError(`Invalid JSON: ${err.message}`);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(jsonText);
        setSnackbar({ open: true, message: 'JSON copied to clipboard!' });
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                    startIcon={<ContentCopy />}
                    variant="outlined"
                    onClick={handleCopy}
                >
                    Copy JSON
                </Button>
                {!readOnly && (
                    <Button
                        startIcon={<Save />}
                        variant="contained"
                        onClick={handleApply}
                        disabled={!!error}
                    >
                        Apply Changes
                    </Button>
                )}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField
                fullWidth
                multiline
                minRows={20}
                maxRows={40}
                value={jsonText}
                onChange={handleChange}
                InputProps={{
                    readOnly: readOnly,
                    style: { fontFamily: 'monospace', fontSize: '0.875rem' }
                }}
                variant="outlined"
                sx={{ bgcolor: 'background.paper' }}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
};

export default JsonViewTab;
